import React, { useEffect } from 'react';
import * as Sentry from '@sentry/browser';
import { useHistory, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from 'ui';
import { useTranscriptDashboard } from 'hooks/transcripts/queries';
import { useAssignTranscript, useUnassignTranscript } from 'hooks/transcripts/mutations';
import { useProjectUsers, useCurrentUser } from 'hooks/users/queries';
import PageLoadingComponent from './PageLoadingComponent';
import ErrorDisplay from './ErrorDisplay';
import ProjectNotFound from './ProjectNotFound';
import DashboardTable from './transcripts_dashboard/DashboardTable';
import ColorConstants from 'constants/ColorConstants';

function TranscriptsDashboardPage(props) {
  const params = useParams();
  const projectID = props.projectID || params.projectID;
  const history = useHistory();

  const { data: dashboardRows, isLoading, isError, error } = useTranscriptDashboard(projectID);
  // A missing/forbidden project 404s (or 403s) here — show the app's standard
  // Project-not-found page rather than the generic "Oops!" error.
  const status = error?.response?.status || error?.originalError?.response?.status || error?.status;
  const isNotFound = isError && (
    status === 404 || status === 403 || error?.name === 'HTTP404Error' || error?.name === 'HTTP403Error'
  );
  const { data: projectUsers } = useProjectUsers(projectID);
  const { data: currentUser } = useCurrentUser();

  const assignTranscript = useAssignTranscript(projectID);
  const unassignTranscript = useUnassignTranscript(projectID);

  const rows = dashboardRows || [];

  // A project with no transcripts always onboards: every way of reaching the
  // dashboard empty (direct URL, a sidebar click before the transcript list
  // loaded, deleting the last transcript) lands on the add-transcript screen.
  const isEmpty = !isLoading && !isError && rows.length === 0;
  useEffect(() => {
    if (isEmpty) history.replace(`/projects/${projectID}/transcripts/new`);
  }, [isEmpty, history, projectID]);

  const people = projectUsers
    ? [projectUsers.owner, ...(projectUsers.collaborators || [])].filter(Boolean)
    : [];

  const onToggleAssign = (row, person, assigned) => {
    if (assigned) {
      unassignTranscript.mutate({ transcriptId: row.id, userId: person.id });
    } else {
      assignTranscript.mutate({ transcriptId: row.id, user: person });
    }
  };

  // Reuse the app's standard page states: the centered spinner and the
  // full-page "Oops!" error (same as ComparisonViewContainer / ErrorBoundary).
  // isEmpty renders the spinner too: the redirect above is about to fire, so
  // the dashboard chrome must not flash first.
  if (isLoading || isEmpty) {
    return <PageLoadingComponent />;
  }

  if (isNotFound) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <ProjectNotFound />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* hideGoBack: the sidebar is still up, so "Go back to Delve" is redundant here */}
        <ErrorDisplay report={() => Sentry.showReportDialog()} hideGoBack />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', backgroundColor: ColorConstants.WHITE }}>
      <Box sx={{ pl: '48px', pr: '48px', py: 4, width: 'fit-content', minWidth: 'min(100%, 820px)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          {props.hasEditPermission && (
            <Button variant="blue" onClick={() => history.push(`/projects/${projectID}/transcripts/new`)}>
              + Add Transcript
            </Button>
          )}
        </Box>

        {rows.length > 0 && (
          <DashboardTable
            rows={rows}
            currentUserId={currentUser?.id}
            people={people}
            hasEditPermission={props.hasEditPermission}
            projectID={projectID}
            onToggleAssign={onToggleAssign}
          />
        )}
      </Box>
    </Box>
  );
}

export default TranscriptsDashboardPage;
